/**
 * Pre-computed aircraft position layouts for each demo scenario.
 *
 * Every scenario maps aircraft ID → {position [x,y,z] feet, rotation degrees}.
 * The hero aircraft ID is 'alloc-hero'. Fleet IDs match fleetConfig.ts.
 *
 * Layout rules kept realistic:
 * - +Z = south (toward hangar doors)
 * - Max Yield: aircraft packed tight, wings interleaved at different Y heights
 * - Op Ease: wider spacing, straight tow paths
 * - Hero near door for early morning, deeper for long-term
 */

export type DepartureWindow = 'morning' | 'midday' | 'long_term'
export type Strategy = 'max_yield' | 'op_ease'
export type ScenarioId =
  | 'baseline'
  | 'max_yield_morning'
  | 'max_yield_midday'
  | 'max_yield_long_term'
  | 'op_ease_morning'
  | 'op_ease_midday'

export interface AircraftPose {
  position: [number, number, number]
  rotation: number
}

export interface Scenario {
  id: ScenarioId
  strategy: Strategy | null
  departureWindow: DepartureWindow | null
  label: string
  description: string
  poses: Record<string, AircraftPose>
}

// ---------------------------------------------------------------------------
// Baseline — no hero, fleet as-is (spread somewhat loosely, Friday evening)
// ---------------------------------------------------------------------------
const BASELINE: Scenario = {
  id: 'baseline',
  strategy: null,
  departureWindow: null,
  label: 'Current State',
  description: 'Friday evening. Eight aircraft parked. Hangar looks full.',
  poses: {
    'alloc-kr1': { position: [-85, 0, -65], rotation: 180 },
    'alloc-cx1': { position: [-28, 0, -62], rotation: 180 },
    'alloc-ch1': { position: [42, 0, -60], rotation: 180 },
    'alloc-g2':  { position: [90, 0, -55], rotation: 180 },
    'alloc-ph1': { position: [-75, 0, 5],  rotation: 180 },
    'alloc-cj1': { position: [-18, 0, 8],  rotation: 180 },
    'alloc-pc1': { position: [45, 0, 10],  rotation: 180 },
    'alloc-hj1': { position: [88, 0, 18],  rotation: 180 },
  },
}

// ---------------------------------------------------------------------------
// MAX YIELD — MORNING DEPARTURE (06:00)
// Hero near doors, nose-out. Tight packing of existing fleet to free premium
// front-row real estate. Wings interleaved to maximize density.
// ---------------------------------------------------------------------------
const MAX_YIELD_MORNING: Scenario = {
  id: 'max_yield_morning',
  strategy: 'max_yield',
  departureWindow: 'morning',
  label: 'Max Yield — 06:00 Departure',
  description: 'Dense pack. Hero placed right at the doors, nose-out for a 3-min pull. Fleet rearranged to fill every inch.',
  poses: {
    // Back row — squeezed together, wings crossing over smaller tails
    'alloc-kr1': { position: [-88, 0, -68], rotation: 180 },
    'alloc-cx1': { position: [-30, 0, -72], rotation: 180 },
    'alloc-ch1': { position: [38, 0, -68], rotation: 180 },
    'alloc-g2':  { position: [90, 0, -65], rotation: 180 },
    // Front row — pushed back and sideways to clear hero's wide wings
    'alloc-ph1': { position: [-82, 0, -12], rotation: 180 },
    'alloc-cj1': { position: [-25, 0, -8],  rotation: 200 },
    'alloc-pc1': { position: [50, 0, -10],  rotation: 160 },
    'alloc-hj1': { position: [90, 0, -5],   rotation: 180 },
    // Hero — right at the door, nose pointing south (0 = north, 180 = south exit)
    'alloc-hero': { position: [0, 0, 50],   rotation: 0 },
  },
}

// ---------------------------------------------------------------------------
// MAX YIELD — MIDDAY DEPARTURE (13:00)
// Hero can go deeper — doors are free for morning transients that generate
// daily cash. Hero has a clear straight corridor to the exit.
// ---------------------------------------------------------------------------
const MAX_YIELD_MIDDAY: Scenario = {
  id: 'max_yield_midday',
  strategy: 'max_yield',
  departureWindow: 'midday',
  label: 'Max Yield — 13:00 Departure',
  description: 'Hero tucked mid-hangar. Premium door space freed for two Citation transients arriving at 08:00. Clear straight path for 13:00 pull.',
  poses: {
    // Back row remains dense
    'alloc-kr1': { position: [-88, 0, -68], rotation: 180 },
    'alloc-cx1': { position: [-30, 0, -72], rotation: 180 },
    'alloc-ch1': { position: [38, 0, -68], rotation: 180 },
    'alloc-g2':  { position: [90, 0, -65], rotation: 180 },
    // Front row angled to create a central corridor for hero
    'alloc-ph1': { position: [-82, 0, -5],  rotation: 195 },
    'alloc-cj1': { position: [-82, 0, 25],  rotation: 180 },
    'alloc-pc1': { position: [85, 0, -5],   rotation: 165 },
    'alloc-hj1': { position: [85, 0, 22],   rotation: 180 },
    // Hero centered, mid-depth, clear corridor
    'alloc-hero': { position: [0, 0, -20],  rotation: 180 },
  },
}

// ---------------------------------------------------------------------------
// MAX YIELD — LONG TERM (4+ days)
// Hero buried deepest possible corner. Wings overlap smaller aircraft.
// Entire door zone freed for 2+ transient overnights = max revenue.
// ---------------------------------------------------------------------------
const MAX_YIELD_LONG_TERM: Scenario = {
  id: 'max_yield_long_term',
  strategy: 'max_yield',
  departureWindow: 'long_term',
  label: 'Max Yield — Long-Term Storage (4 days)',
  description: "Hero buried deep — wings overlap turboprops at different elevations. Two transient spots freed at the door earn $600/night each. +$4,800 over 4 days.",
  poses: {
    // Hero goes all the way to the back corner
    'alloc-hero': { position: [10, 0, -72],  rotation: 180 },
    // Smaller aircraft pack around the hero's wings
    'alloc-kr1':  { position: [-72, 0, -65], rotation: 180 },
    'alloc-pc1':  { position: [-72, 0, -20], rotation: 180 },
    'alloc-cx1':  { position: [-25, 0, -28], rotation: 195 },
    'alloc-cj1':  { position: [-25, 0, 15],  rotation: 180 },
    'alloc-hj1':  { position: [85, 0, -25],  rotation: 180 },
    // Challenger and G280 flanking hero (their tail height clears Global's wings)
    'alloc-ch1':  { position: [-62, 0, -68], rotation: 175 },
    'alloc-g2':   { position: [80, 0, -60],  rotation: 180 },
  },
}

// ---------------------------------------------------------------------------
// OPERATIONAL EASE — MORNING DEPARTURE (06:00)
// Wide spacing, hero right at the door nose-out. Only 2 tow moves needed.
// ---------------------------------------------------------------------------
const OP_EASE_MORNING: Scenario = {
  id: 'op_ease_morning',
  strategy: 'op_ease',
  departureWindow: 'morning',
  label: 'Operational Ease — 06:00 Departure',
  description: 'Wide corridors. Hero at the door, ready in 3 minutes with one tug and no wing walkers required.',
  poses: {
    // Back row with comfortable spacing
    'alloc-kr1': { position: [-88, 0, -62], rotation: 180 },
    'alloc-cx1': { position: [-22, 0, -62], rotation: 180 },
    'alloc-ch1': { position: [42, 0, -62],  rotation: 180 },
    'alloc-g2':  { position: [90, 0, -58],  rotation: 180 },
    // Front row well spaced, all nose-in for easy maneuver
    'alloc-ph1': { position: [-80, 0, 12],  rotation: 180 },
    'alloc-cj1': { position: [-22, 0, 15],  rotation: 180 },
    'alloc-pc1': { position: [40, 0, 15],   rotation: 180 },
    'alloc-hj1': { position: [90, 0, 20],   rotation: 180 },
    // Hero — straight at the door, 4m clearance on each side
    'alloc-hero': { position: [0, 0, 52],   rotation: 0 },
  },
}

// ---------------------------------------------------------------------------
// OPERATIONAL EASE — MIDDAY DEPARTURE (13:00)
// ---------------------------------------------------------------------------
const OP_EASE_MIDDAY: Scenario = {
  id: 'op_ease_midday',
  strategy: 'op_ease',
  departureWindow: 'midday',
  label: 'Operational Ease — 13:00 Departure',
  description: 'Hero mid-hangar with a wide, straight exit path. Staff can handle it during normal shift hours with minimal disruption.',
  poses: {
    'alloc-kr1': { position: [-88, 0, -62], rotation: 180 },
    'alloc-cx1': { position: [-22, 0, -62], rotation: 180 },
    'alloc-ch1': { position: [42, 0, -62],  rotation: 180 },
    'alloc-g2':  { position: [90, 0, -58],  rotation: 180 },
    'alloc-ph1': { position: [-82, 0, 5],   rotation: 180 },
    'alloc-cj1': { position: [-82, 0, 38],  rotation: 180 },
    'alloc-pc1': { position: [85, 0, 5],    rotation: 180 },
    'alloc-hj1': { position: [85, 0, 38],   rotation: 180 },
    // Hero centered mid-depth, straight corridor
    'alloc-hero': { position: [0, 0, -18],  rotation: 180 },
  },
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const SCENARIOS: Record<ScenarioId, Scenario> = {
  baseline: BASELINE,
  max_yield_morning: MAX_YIELD_MORNING,
  max_yield_midday: MAX_YIELD_MIDDAY,
  max_yield_long_term: MAX_YIELD_LONG_TERM,
  op_ease_morning: OP_EASE_MORNING,
  op_ease_midday: OP_EASE_MIDDAY,
}

/** Which scenario to show given a strategy + departure window combo */
export function resolveScenarioId(
  strategy: Strategy,
  departureWindow: DepartureWindow
): ScenarioId {
  if (strategy === 'max_yield') {
    if (departureWindow === 'morning') return 'max_yield_morning'
    if (departureWindow === 'midday') return 'max_yield_midday'
    return 'max_yield_long_term'
  }
  // op_ease
  if (departureWindow === 'morning') return 'op_ease_morning'
  if (departureWindow === 'midday') return 'op_ease_midday'
  // No long-term op_ease scenario — fall back to max_yield_long_term
  return 'max_yield_long_term'
}
